import { FC, FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Balance, Delete, Edit, Search } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { useLazyGetInternalGameDocumentQuery } from '../../../../api/games'
import { getRoundedPowerscore } from '../../helpers/gameListHelpers'
import { GameMarketsCell } from '../GameMarketsCell/GameMarketsCell'
import { GameNameCell } from '../GameNameCell/GameNameCell'

const searchMarketIso = 'us'

type GameIdSearchDialogProps = {
  open: boolean
  onClose: () => void
  // Each closes the search first; the page opens its own dialogs for the game.
  onEdit: (game: Game) => void
  onDelete: (game: Game) => void
  onViewAnalyses: (game: Game) => void
}

export const GameIdSearchDialog: FC<GameIdSearchDialogProps> = ({ open, onClose, onEdit, onDelete, onViewAnalyses }) => {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [searched, setSearched] = useState('')
  const [readGame, gameQuery] = useLazyGetInternalGameDocumentQuery()

  useEffect(() => {
    if (!open) return
    setText('')
    setSearched('')
  }, [open])

  // `currentData` is only the answer for the last searched id, so a failed search can't show the previous game.
  const { currentData } = gameQuery
  const game = useMemo(() => (searched && currentData ? new Game(currentData as unknown as Game) : undefined), [currentData, searched])

  const search = (event?: FormEvent) => {
    event?.preventDefault()
    const id = text.trim()
    if (!id) return
    setSearched(id)
    readGame(id)
  }

  const isSearching = !!searched && gameQuery.isFetching

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>{t('internal-analysts:games_search_by_id')}</GRDialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Stack component="form" direction="row" spacing={2} onSubmit={search}>
            <TextField
              size="small"
              autoFocus
              fullWidth
              label={t('internal-analysts:games_id_search_label')}
              value={text}
              disabled={isSearching}
              onChange={(event) => setText(event.target.value)}
            />
            <Button type="submit" variant="contained" startIcon={<Search />} disabled={isSearching || !text.trim()}>
              {t('internal-analysts:games_id_search_button')}
            </Button>
          </Stack>
          {isSearching ? (
            <Skeleton variant="rectangular" height={72} />
          ) : game ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('internal-common:icon')}</TableCell>
                  <TableCell>{t('internal-common:name')}</TableCell>
                  <TableCell>{t('internal-common:publisher')}</TableCell>
                  <TableCell>{t('internal-common:powerscore')}</TableCell>
                  <TableCell>{t('internal-common:stage')}</TableCell>
                  <TableCell>{t('internal-common:markets')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="small" />
                  </TableCell>
                  <TableCell>
                    <GameNameCell game={game} />
                  </TableCell>
                  <TableCell>{game.artist || '---'}</TableCell>
                  <TableCell>{getRoundedPowerscore(game, searchMarketIso) ?? '---'}</TableCell>
                  <TableCell>{game.stage || '---'}</TableCell>
                  <TableCell>
                    <GameMarketsCell game={game} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('internal-analysts:games_view_analyses')}>
                      <IconButton color="primary" aria-label={t('internal-analysts:games_view_analyses')} onClick={() => onViewAnalyses(game)}>
                        <Balance />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('internal-analysts:games_edit')}>
                      <IconButton color="primary" aria-label={t('internal-analysts:games_edit')} onClick={() => onEdit(game)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('internal-analysts:games_delete')}>
                      <IconButton aria-label={t('internal-analysts:games_delete')} color="error" onClick={() => onDelete(game)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            searched && <Typography fontWeight="bold">{t('internal-analysts:games_id_search_none')}</Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
